import React from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Box, Button, Grid2 } from '@mui/material';
import { ImageDropzone, RadioGroup, TextInput } from '@utriper/core-ui';
import Title from 'modules/Pricing/components/Title';
import { Container, Label } from 'modules/Experience/ExperienceWizard/styles';

const CONTENT_TYPE = {
    IMAGE: 1,
    VIDEO: 2,
};

const AddMultimediaForm = ({ onAddMultimedia = () => {}, onClose = () => {} }) => {
    const { t } = useTranslation();
    const translate = (key) => t(`add_multimedia_form.${key}`);
    const contentTypeOptions = [
        {
            id: CONTENT_TYPE.IMAGE,
            label: translate('content_type_options.image'),
        },
        {
            id: CONTENT_TYPE.VIDEO,
            label: translate('content_type_options.youtube'),
        },
    ];

    // TODO: MOVE TO CORE
    const extractYouTubeId = (videoURL = '') => {
        try {
            const url = new URL(videoURL);

            // /shorts/abc123
            if (url.pathname.startsWith('/shorts/')) {
                return url.pathname.replace('/shorts/', '').split('?')[0];
            }

            // watch?v=abc123
            if (url.searchParams.get('v')) {
                return url.searchParams.get('v');
            }

            // youtu.be/abc123
            if (url.hostname.includes('youtu.be')) {
                return url.pathname.substring(1);
            }

            return null;
        } catch (error) {
            return null;
        }
    };

    const validateForm = ({ contentType, selectedFiles = [], videoURL }) => {
        const errors = {};
        const cType = parseInt(contentType);

        if (cType === CONTENT_TYPE.IMAGE && selectedFiles?.length <= 0) {
            errors.selectedFiles = translate('errors.no_image');
        }

        if (cType === CONTENT_TYPE.VIDEO && (!videoURL || videoURL?.trim() === '')) {
            errors.videoURL = translate('errors.no_video');
            return errors;
        }

        if (cType === CONTENT_TYPE.VIDEO) {
            const videoId = extractYouTubeId(videoURL);

            if (!videoId || videoId?.trim() === '' || videoId === videoURL) {
                errors.videoURL = translate('errors.invalid_url');
            }
        }

        return errors;
    };

    const handleSubmit = async ({ contentType, selectedFiles = [], videoURL }) => {
        let file = null;
        if (parseInt(contentType) === CONTENT_TYPE.IMAGE) {
            await onAddMultimedia({ file: selectedFiles, videoId: null, isVideo: false });
            onClose();
            return;
        }

        const videoId = extractYouTubeId(videoURL);
        await onAddMultimedia({ file: null, videoId, isVideo: true });
        onClose();
    };

    return (
        <Form
            onSubmit={handleSubmit}
            validate={validateForm}
            initialValues={{
                contentType: CONTENT_TYPE.IMAGE,
                selectedFiles: [],
            }}
            render={({ handleSubmit, form, values, errors }) => {
                const handleSelectFile = (selectedFiles = []) => {
                    form.change('selectedFiles', selectedFiles);
                };

                return (
                    <form onSubmit={handleSubmit} noValidate style={{ width: '100%', marginTop: '16px' }}>
                        <Box component="section">
                            <Title title={translate('title')} description={translate('description')} />

                            {/* Form */}
                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                                {/* Content Type */}
                                <Container>
                                    <Label>{translate('content_type')}</Label>
                                    <Field name="contentType" component={RadioGroup} options={contentTypeOptions} row />
                                </Container>

                                {parseInt(values?.contentType) === CONTENT_TYPE.IMAGE ? (
                                    <Container>
                                        <Label>{translate('upload_image')}</Label>
                                        <ImageDropzone multiple={true} onFilesSelected={handleSelectFile} />
                                    </Container>
                                ) : (
                                    <Container>
                                        <Label>{translate('youtube_url')}</Label>
                                        <Field name="videoURL" component={TextInput} placeholder="https://www.youtube.com/watch?v=..." />
                                    </Container>
                                )}
                            </Grid2>

                            <Grid2 container justifyContent="flex-end" alignItems="center" gap="16px" mt="32px" flexWrap="nowrap">
                                <Button
                                    sx={{
                                        border: '1px solid #13131340',
                                        color: '#131313',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        py: '8px',
                                    }}
                                    variant="outlined"
                                    onClick={(e) => {
                                        form.reset(e);
                                        onClose();
                                    }}
                                >
                                    {t('common.cancel')}
                                </Button>
                                <Button
                                    sx={{
                                        backgroundColor: 'var(--color-utriper-blue)',
                                        color: 'white',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        py: '8px',
                                    }}
                                    type="submit"
                                    variant="contained"
                                    onClick={handleSubmit}
                                >
                                    {translate('add_button')}
                                </Button>
                            </Grid2>
                        </Box>
                    </form>
                );
            }}
        />
    );
};

export default AddMultimediaForm;
